import * as React from 'react';
import * as _ from 'lodash';
import {
  Certificate,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  Coverage,
  Footer,
  getUuidV4,
  Logging,
  renderEdit,
  stringToDate,
} from '@vcd-ecm-ui/Common';
import {
  VtxButton,
  VtxCollapse,
  VtxCollapsePanel,
  VtxIcon,
  VtxTooltip,
} from '@vertexinc/vtx-ui-react-component-library';
import CoverageItem from './CoverageItem';
import JurisdictionInformationProps from './JurisdictionInformationProps.interface';
import '../steps.scss';
import SelectedItems from '../../Common/selectedItems';

const controlStyle: ControlStyleClass = null;

const controlStateEditable: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlValidation: ControlValidation = {
  requiredFields: [],
  customMessages: [],
};

const JurisdictionInformation = ({
  gatewayEndPoint,
  step,
  certificate,
  selectedItems,
  jurisdictions,
  onCancel,
  onContinuteSave,
}: JurisdictionInformationProps): JSX.Element => {
  Logging.Debug('Jurisdiction Information');

  // Create a New Coverage
  const createNewCoverage = (): Coverage => {
    // Create a New Coverage
    const newCoverage: Coverage = {
      uuid: getUuidV4(),
      jurisdiction: null,
      issueDate: null,
      reviewDate: null,
      expirationDate: null,
      certificateNumber: null,
      reason: null,
      impositions: null,
    };

    return newCoverage;
  };

  const newCoverage: Coverage = createNewCoverage();

  // Append the New coverage to the existing Coverages
  const [coverages, setCoverages] = React.useState(certificate.coverages ? certificate.coverages.concat([newCoverage]) : [newCoverage]);
  const [activeKey, setActiveKey] = React.useState(newCoverage.uuid);

  // Get the Index of the Coverage using the Key (Uuid)
  const getCoverageIndex = (key: string): number => {
    let index = 0;
    let coverageIndex = -1;

    coverages.forEach((coverage: Coverage) => {
      if (coverage.uuid === key) {
        coverageIndex = index;
      }

      index += 1;
    });

    return coverageIndex;
  };

  // Update the Coverage
  const onUpdateCoverage = (coverage: Coverage): void => {
    // Update
    Logging.Debug('Update Coverage', coverage);

    // Update the Modified coverage
    const coveragesCopy: Coverage[] = _.cloneDeep(coverages);

    // Replace the Updated Coverage
    const index = getCoverageIndex(coverage.uuid);
    coveragesCopy[index] = _.cloneDeep(coverage);

    setCoverages(coveragesCopy);
  };

  // Remove the Coverage
  const onRemoveCoverage = (coverage: Coverage): void => {
    // Remove the Coverage
    Logging.Info('Remove Coverage', coverage);

    // Update the Modified coverage
    const coveragesCopy: Coverage[] = _.cloneDeep(coverages);

    // Remove the Coverage
    const index = getCoverageIndex(coverage.uuid);
    coveragesCopy.splice(index, 1);

    // Select the last Coverage
    setActiveKey(coverages[coverages.length - 1].uuid);

    setCoverages(coveragesCopy);
  };

  // Add a New Coverage
  const onAddCoverage = (): void => {
    // Create a New Coverage
    const coverage: Coverage = createNewCoverage();

    Logging.Debug('Add Another Coverage', coverage);

    const coveragesCopy: Coverage[] = _.cloneDeep(coverages);
    coveragesCopy.push(coverage);

    setActiveKey(coverage.uuid);

    setCoverages(coveragesCopy);
  };

  // Render Coverage
  // eslint-disable-next-line arrow-body-style
  const renderCoverage = (coverage: Coverage): JSX.Element => {
    Logging.Debug('Render Coverage', coverage);
    // Render the Coverage
    return (
      <VtxCollapsePanel
        header={coverage?.jurisdiction?.name || 'Select a Jurisdiction'}
        key={coverage.uuid}
        showArrow
        className="coveragesCollapsePanel"
        extra={<VtxTooltip title="Remove the Jurisdiction"><VtxIcon type="delete" onClick={(event) => onRemoveCoverage(coverage)} /></VtxTooltip>}
      >
        <CoverageItem
          coverage={coverage}
          certificate={certificate}
          onUpdateCoverage={onUpdateCoverage}
          jurisdictions={jurisdictions}
          exemptionReason={selectedItems?.exemptionReason}
          coverages={coverages}
        />
      </VtxCollapsePanel>
    );
  };

  // Render Coverages
  // eslint-disable-next-line arrow-body-style
  const renderCoverages = (): JSX.Element[] => {
    // eslint-disable-next-line arrow-body-style
    return coverages.map((coverage: Coverage) => {
      return renderCoverage(coverage);
    });
  };

  // Coverage Selected
  const onCoverageSelected = (key: string): void => {
    Logging.Debug('Coverage Selected', key);
    setActiveKey(key);
  };

  // Continue / Save Button
  const onSaveContinue = (): void => {
    const copyCertificate: Certificate = _.cloneDeep(certificate);
    const copySelectedItems: SelectedItems = _.cloneDeep(selectedItems);

    // Update the Coverages
    copyCertificate.coverages = coverages;

    Logging.Info('Continue to Step 3:', copyCertificate, selectedItems);

    onContinuteSave(step, copyCertificate, copySelectedItems);
  };

  // Validate the Step
  const validateStep = (): boolean => {
    let validated = true;

    const customerStartDate = stringToDate(selectedItems?.customer.effectiveDate);
    const customerEndDate = stringToDate(selectedItems?.customer.endDate);

    coverages.forEach((coverage: Coverage) => {
      // Only Validate once Jurisdiction Selected
      if (coverage.jurisdiction) {
        const instances = coverages.filter((coverageItem: Coverage) => coverageItem.jurisdiction?.name === coverage.jurisdiction.name).length;

        // Check if it is unique
        if (instances > 1) {
          validated = false;
          Logging.Info('Jurisdiction not Unique');
        }

        const issueDate = coverage.issueDate ? stringToDate(coverage.issueDate) : null;
        const reviewDate = coverage.reviewDate ? stringToDate(coverage.reviewDate) : null;

        if (customerStartDate) {
          // Validate Issue Date
          if (issueDate) {
            if (issueDate < customerStartDate) {
              validated = false;
              Logging.Info('Invalid Issue Date (Customer Start)');
            }
          }

          // Validate Review Date
          if (reviewDate) {
            if (reviewDate < customerStartDate) {
              validated = false;
              Logging.Info('Invalid Review Date (Customer Start)');
            }
          }

          // Validate Expiration Date
          if (coverage.expirationDate) {
            const expirationDate = stringToDate(coverage.expirationDate);

            if (expirationDate < customerStartDate) {
              validated = false;
              Logging.Info('Invalid Expiration Date (Customer Start)');
            }
          }
        }

        if (customerEndDate) {
          // Validate Issue Date
          if (issueDate) {
            if (issueDate > customerEndDate) {
              validated = false;
              Logging.Info('Invalid Issue Date (Customer End)');
            }
          }

          // Validate Review Date
          if (reviewDate) {
            if (reviewDate > customerEndDate) {
              validated = false;
              Logging.Info('Invalid Review Date (Customer End)');
            }
          }
        }

        if (issueDate && reviewDate) {
          if (reviewDate < issueDate) {
            validated = false;
            Logging.Info('Invalid Review Date');
          }
        }
      } else {
        validated = false;
        Logging.Info('Jurisdiction is Required');
      }
    });

    return validated;
  };

  const canSaveContinue = validateStep();

  return (
    <>
      <div className="stepContent">
        {
          coverages.length > 1
            ? (
              <div className="coveragesCollapseContainer">
                <VtxCollapse
                  accordion
                  onChange={onCoverageSelected}
                  className="coveragesCollapse"
                  activeKey={activeKey}
                >
                  {renderCoverages()}
                </VtxCollapse>
              </div>
            )
            : (
              <CoverageItem
                coverage={coverages[0]}
                certificate={certificate}
                onUpdateCoverage={onUpdateCoverage}
                jurisdictions={jurisdictions}
                exemptionReason={selectedItems?.exemptionReason}
                coverages={coverages}
              />
            )
        }
        <div className="buttonRow">
          <VtxButton
            id="addButton"
            icon={<VtxIcon type="plus" />}
            onClick={onAddCoverage}
            type="default"
          >
            Add Jurisdiction
          </VtxButton>
        </div>
      </div>
      <Footer onCancel={onCancel} saveEnabled={canSaveContinue} onSave={onSaveContinue} saveText="Continue" />
    </>
  );
};

export default JurisdictionInformation;
